"use client";

import { ProductTileTrackingWrapperProps } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileTrackingWrapper/ProductTileTrackingWrapperProps";
import { useGetProductTileClickImpressions } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/productTileActions/useGetProductTileClickImpressions";
import { usePrefetchOnHover } from "@faire/retailer-visitor-shared/hooks/pdpPrefetching/usePDPPrefetchOnHover";
import { useProductTileViewImpression } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileViewImpression";
import { assignSettingOnEvent } from "@faire/retailer-visitor-shared/settings/assignSettingOnEvent";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useEffectEvent } from "@faire/web--source/ui/hooks/useEffectEvent";
import React from "react";

export const ProductTileTrackingWrapper = ({
  data: {
    productToken,
    isPromoted,
    impressionId,
    promotedBy,
    badgeList,
    bestImage,
    images,
    settingsToAssignOnView,
    recordProductTileImpressionForAds = true,
  },
  brandToken,
  children,
}: {
  data: ProductTileTrackingWrapperProps;
  children: React.ReactNode;
  brandToken: string | undefined;
}) => {
  const trackProductTileProps = useProductTileViewImpression({
    productToken,
    promotedBy,
    impressionId,
    turnOffTracking: !recordProductTileImpressionForAds,
  });
  const {
    recordProductTileRightClickEvent,
    recordProductTileMiddleClickEvent,
    recordProductTileTouchStartEvent,
  } = useGetProductTileClickImpressions({
    token: productToken,
    isPromoted,
    impressionId,
    promotedBy,
    badgeList,
    bestImage,
    images,
  });

  const settingAssignmentRef = useDoActionWhenVisible(() => {
    settingsToAssignOnView?.forEach((setting) => {
      assignSettingOnEvent(setting);
    });
  });

  const handleOnAuxClick = useEffectEvent(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.button === 1) {
        recordProductTileMiddleClickEvent();
      } else if (event.button === 2) {
        recordProductTileRightClickEvent();
      }
    }
  );

  const handleOnTouchStart = () => {
    recordProductTileTouchStartEvent();
  };

  const { onMouseEnter, onMouseLeave } = usePrefetchOnHover({
    productToken: productToken,
    bestImage: bestImage,
    images: images ?? [],
    brandToken: brandToken,
  });

  return (
    <div
      {...trackProductTileProps}
      onAuxClick={handleOnAuxClick}
      onTouchStart={handleOnTouchStart}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={(element) => {
        settingAssignmentRef(element);
        trackProductTileProps?.ref?.(element);
      }}
    >
      {children}
    </div>
  );
};
